/**
 * API calls for the pharmacy Tasks worklist.
 *
 * Uses the shared authenticated `api` client (injects the bearer JWT). The
 * backend scopes every response to the caller's facility/org via the JWT, so
 * these calls never send org_id.
 */

import { api } from "@risalabs_frontend_org/api-client";

import { API_ENDPOINTS } from "../../enums/apiUrls";
import { OrganizationType } from "../../enums/organizationTypes";
import {
  FilterOptionsResponse,
  OncoEMRUserSearchResponse,
  OncoEMRUserSearchResult,
  TaskAddRecipientRequest,
  TaskDetailResponse,
  TaskRemoveRecipientRequest,
  TaskReplyRequest,
  TaskStatusUpdateRequest,
  TaskWorklistRequest,
  TaskWorklistResponse,
  TaskWritebackResponse,
  TaskWritebackRetryRequest,
  TaskWritebackStatus,
} from "./taskTypes";

/** Generate an idempotency key for a writeback action (collapses double-clicks). */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Fetch a filtered, sorted, paginated page of tasks.
 *
 * @param request - Worklist query (search + filters + sort + pagination)
 * @param signal - Optional AbortSignal for cancellation
 * @returns Page of tasks + total matching count
 */
export async function fetchTaskWorklist(
  request: TaskWorklistRequest,
  signal?: AbortSignal,
): Promise<TaskWorklistResponse> {
  return api.post<TaskWorklistRequest, TaskWorklistResponse>(
    API_ENDPOINTS.PHARMACY_TASKS_WORKLIST,
    request,
    signal,
  );
}

/**
 * Fetch distinct facet values for the filters dialog.
 *
 * @param signal - Optional AbortSignal for cancellation
 * @returns Observed distinct values per facet
 */
export async function fetchTaskFilterOptions(
  orgId: string,
  signal?: AbortSignal,
): Promise<FilterOptionsResponse> {
  const url = `${API_ENDPOINTS.PHARMACY_TASKS_FILTER_OPTIONS}?org_id=${encodeURIComponent(orgId)}`;
  return api.get<FilterOptionsResponse>(url, signal);
}

/**
 * Fetch one task's header, conversation thread, and document ids.
 *
 * @param taskRowId - pharmacy_tasks.id
 * @param signal - Optional AbortSignal for cancellation
 * @returns Task header + thread + document ids
 */
export async function fetchTaskDetail(
  taskRowId: string,
  orgId: string,
  signal?: AbortSignal,
): Promise<TaskDetailResponse> {
  const url = `${API_ENDPOINTS.PHARMACY_TASK_DETAIL}/${taskRowId}?org_id=${encodeURIComponent(orgId)}`;
  return api.get<TaskDetailResponse>(url, signal);
}

/**
 * Post a freestyle reply to a task's OncoEMR thread.
 *
 * Generates a fresh idempotency key per call unless one is supplied (a Retry
 * reuses the original key so the send stays idempotent).
 *
 * @param taskRowId - pharmacy_tasks.id of the task to reply to
 * @param message - Reply body text
 * @param idempotencyKey - Optional key to reuse (for retrying the same send)
 * @returns Writeback result (status sent | failed)
 */
export async function sendTaskReply(
  taskRowId: string,
  message: string,
  idempotencyKey?: string,
): Promise<TaskWritebackResponse> {
  return api.post<TaskReplyRequest, TaskWritebackResponse>(
    API_ENDPOINTS.PHARMACY_TASK_REPLY,
    {
      org_id: OrganizationType.ASTERA_PRIMARY_CARE,
      task_row_id: taskRowId,
      message,
      idempotency_key: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

/**
 * Change a task's OncoEMR status (Open / InProgress / Complete).
 *
 * @param taskRowId - pharmacy_tasks.id of the task to update
 * @param status - New OncoEMR status
 * @param idempotencyKey - Optional key to reuse (for retrying the same change)
 * @returns Writeback result (status sent | failed)
 */
export async function updateTaskStatus(
  taskRowId: string,
  status: TaskWritebackStatus,
  idempotencyKey?: string,
): Promise<TaskWritebackResponse> {
  return api.post<TaskStatusUpdateRequest, TaskWritebackResponse>(
    API_ENDPOINTS.PHARMACY_TASK_STATUS,
    {
      org_id: OrganizationType.ASTERA_PRIMARY_CARE,
      task_row_id: taskRowId,
      status,
      idempotency_key: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

/**
 * Add a recipient (OncoEMR user) to a task.
 *
 * Generates a fresh idempotency key per call unless one is supplied (a Retry
 * reuses the original key so the send stays idempotent).
 *
 * @param taskRowId - pharmacy_tasks.id of the task to add a recipient to
 * @param recipientId - OncoEMR user id to add as a recipient
 * @param idempotencyKey - Optional key to reuse (for retrying the same send)
 * @returns Writeback result (status sent | failed)
 */
export async function addTaskRecipient(
  taskRowId: string,
  recipientId: string,
  idempotencyKey?: string,
): Promise<TaskWritebackResponse> {
  return api.post<TaskAddRecipientRequest, TaskWritebackResponse>(
    API_ENDPOINTS.PHARMACY_TASK_ADD_RECIPIENT,
    {
      org_id: OrganizationType.ASTERA_PRIMARY_CARE,
      task_row_id: taskRowId,
      recipient_id: recipientId,
      idempotency_key: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

/**
 * Remove a recipient (OncoEMR user) from a task.
 *
 * Generates a fresh idempotency key per call unless one is supplied (a Retry
 * reuses the original key so the send stays idempotent).
 *
 * @param taskRowId - pharmacy_tasks.id of the task to remove a recipient from
 * @param recipientId - OncoEMR user id to remove as a recipient
 * @param idempotencyKey - Optional key to reuse (for retrying the same send)
 * @returns Writeback result (status sent | failed)
 */
export async function removeTaskRecipient(
  taskRowId: string,
  recipientId: string,
  idempotencyKey?: string,
): Promise<TaskWritebackResponse> {
  return api.post<TaskRemoveRecipientRequest, TaskWritebackResponse>(
    API_ENDPOINTS.PHARMACY_TASK_REMOVE_RECIPIENT,
    {
      org_id: OrganizationType.ASTERA_PRIMARY_CARE,
      task_row_id: taskRowId,
      recipient_id: recipientId,
      idempotency_key: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

/**
 * Cached full OncoEMR user directory per org (recipient picker name lookup).
 *
 * The search endpoint returns the whole enabled directory when `q` is empty, so
 * we fetch it once per org and filter locally instead of calling per keystroke.
 */
const userDirectoryCache = new Map<
  string,
  Promise<OncoEMRUserSearchResult[]>
>();

/**
 * Fetch (and cache) the org's enabled OncoEMR user directory for the picker.
 *
 * Caches the in-flight promise (not just the resolved array) so the chips and
 * picker mounting together share one request instead of both downloading the
 * whole directory. A failure is evicted so the next open retries. The shared
 * fetch is intentionally not tied to a caller's signal — one consumer unmounting
 * must not cancel the request the other consumer is still awaiting.
 *
 * @param orgId - Data org id to scope the directory to
 * @returns All enabled users with their OncoEMR UserIds and display names
 */
export async function fetchOncoEmrUserDirectory(
  orgId: string,
): Promise<OncoEMRUserSearchResult[]> {
  const inFlight = userDirectoryCache.get(orgId);
  if (inFlight) return inFlight;
  const url = `${API_ENDPOINTS.PHARMACY_ONCOEMR_USERS_SEARCH}?org_id=${encodeURIComponent(orgId)}`;
  const pending = api
    .get<OncoEMRUserSearchResponse>(url)
    .then((response) => response.data ?? [])
    .catch((err) => {
      userDirectoryCache.delete(orgId); // don't cache a failure
      throw err;
    });
  userDirectoryCache.set(orgId, pending);
  return pending;
}

/**
 * Re-drive a failed writeback action using its stored payload.
 *
 * @param idempotencyKey - Idempotency key of the failed action to retry
 * @returns Writeback result after re-drive (status sent | failed)
 */
export async function retryTaskWriteback(
  idempotencyKey: string,
): Promise<TaskWritebackResponse> {
  return api.post<TaskWritebackRetryRequest, TaskWritebackResponse>(
    API_ENDPOINTS.PHARMACY_TASK_WRITEBACK_RETRY,
    {
      org_id: OrganizationType.ASTERA_PRIMARY_CARE,
      idempotency_key: idempotencyKey,
    },
  );
}
